import {
  EncounterDiagnosisSchema,
  EncounterNoteAddendumSchema,
  FdbScreeningOverrideSchema,
  PatientCoverageUpdateInputSchema,
  PhysicianSummarySchema,
  PmhItemSchema,
  PrescriptionChangeLinkSchema,
  PrescriptionChangeVisitSchema,
  PrescriptionRenewalLinkSchema,
  PrescriptionRenewalVisitSchema,
  RecentEncounterDiagnosisSchema,
  StructuredSigSchema,
} from "@emr/contracts";
import { z } from "zod";

/**
 * Client-side contracts for the persisted pilot clinical-workspace routes.
 *
 * Every response shape mirrors the authoritative Supabase RPC jsonb payloads:
 * - private.appointment_queue_response / api.get_today_board
 * - api.create_walk_in
 * - api.get_patient_workspace
 * - api.save_encounter_note_draft / api.sign_encounter_note
 * - api.get_current_workforce_profile / api.update_current_workforce_profile
 * - api.create_and_sign_printable_prescription
 *
 * Responses are parsed tolerantly (loose objects keep unknown keys) and the
 * client accepts either the raw RPC json or an envelope { result: <json> }.
 */

const UuidSchema = z.string().uuid();
const Sha256HashSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);

/** Raised when the authoritative record moved (HTTP 409 / Postgres PT409). */
export class ClinicalWriteConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ClinicalWriteConflictError";
  }
}

export function isClinicalWriteConflict(error: unknown): error is ClinicalWriteConflictError {
  return error instanceof ClinicalWriteConflictError;
}

/**
 * Accept the raw RPC json, the route's documented envelope (for example
 * `{ patients: [...] }` or `{ note: {...} }`), or a generic `{ result: <json> }`
 * envelope. Unwraps only when the payload is an object with exactly one key and
 * that key is the expected envelope key or `result`.
 */
export function unwrapRpcResult(value: unknown, envelopeKey?: string): unknown {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record);
    if (keys.length === 1) {
      const [key] = keys;
      if (key === "result" || (envelopeKey !== undefined && key === envelopeKey)) {
        return record[key as string];
      }
    }
  }
  return value;
}

export const QueueStatusSchema = z.enum([
  "waiting",
  "rooming",
  "ready",
  "with-clinician",
  "wrap-up",
]);
export const QueueUrgencySchema = z.enum(["routine", "soon", "urgent"]);
export const CoverageBadgeSchema = z.enum(["Verified", "Pending", "Cash"]);
export const AdministrativeSexSchema = z.enum(["female", "male", "unknown", "other"]);
export type AdministrativeSex = z.infer<typeof AdministrativeSexSchema>;

/**
 * Administrative sex narrowed to the male/female values the UI renders; the
 * product records but never displays "unknown"/"other".
 */
export function displayedAdministrativeSex(
  value: AdministrativeSex | undefined,
): "female" | "male" | undefined {
  return value === "female" || value === "male" ? value : undefined;
}

/**
 * One persisted queue row. Key names mirror private.appointment_queue_response
 * exactly; jsonb_strip_nulls means genuinely-null fields are absent, so those
 * are optional here.
 */
export const BoardQueueRowSchema = z.looseObject({
  appointmentId: UuidSchema.optional(),
  appointmentVersion: z.number().int().positive().optional(),
  /** Raw clinical.appointments status ('scheduled' rows can be checked in). */
  appointmentStatus: z.string().optional(),
  /** Patient id (the RPC emits it as `id`). */
  id: UuidSchema,
  encounterId: UuidSchema,
  encounterVersion: z.number().int().positive(),
  encounterStatus: z
    .enum(["open", "ready_for_review", "signed", "amended", "cancelled"])
    .optional(),
  encounterSigned: z.boolean().optional(),
  initials: z.string().min(1),
  displayName: z.string().min(1),
  age: z.number().int().nonnegative().optional(),
  /** Always emitted by the current RPC; optional so pre-migration payloads still parse. */
  administrativeSex: AdministrativeSexSchema.optional(),
  reason: z.string(),
  arrivedAt: z.string().optional(),
  waitMinutes: z.number().int().nonnegative().catch(0),
  status: QueueStatusSchema.catch("waiting"),
  urgency: QueueUrgencySchema.catch("routine").optional(),
  room: z.string().optional(),
  clinician: z.string().optional(),
  rosterPhysician: PhysicianSummarySchema.optional(),
  assignedToCurrentActor: z.boolean().optional(),
  tasks: z.number().int().nonnegative().catch(0).optional(),
  coverage: CoverageBadgeSchema.catch("Pending").optional(),
});
export type BoardQueueRow = z.infer<typeof BoardQueueRowSchema>;

export const TodayBoardSchema = z.array(BoardQueueRowSchema);
export type TodayBoard = z.infer<typeof TodayBoardSchema>;

/**
 * Which kind of plan a coverage member ID identifies. The member ID is the
 * one patient health identifier: provincial/federal member IDs print as the
 * health number, private ones as a member ID.
 */
export const CoveragePayerKindSchema = z.enum([
  "provincial_plan",
  "federal_program",
  "private_insurance",
  "other",
]);
export type CoveragePayerKind = z.infer<typeof CoveragePayerKindSchema>;

export const COVERAGE_PAYER_KIND_LABELS: Readonly<Record<CoveragePayerKind, string>> = {
  provincial_plan: "Provincial health plan (OHIP, MSP, …)",
  federal_program: "Federal program (IFH, NIHB, …)",
  private_insurance: "Private insurance",
  other: "Other coverage",
};

/**
 * The coverage api.create_walk_in accepts: the same shape as registration and
 * the demographics writer, with a provincial health card's version code and
 * expiry date (migration 20261007022422).
 */
export const WalkInCoverageSchema = PatientCoverageUpdateInputSchema;
export type WalkInCoverage = z.infer<typeof WalkInCoverageSchema>;

/** Mirrors the p_input contract of api.create_walk_in exactly. */
export const WalkInIntakeInputSchema = z.object({
  legalFirstName: z.string().min(1).max(100),
  legalMiddleName: z.string().min(1).max(100).optional(),
  legalLastName: z.string().min(1).max(100),
  preferredName: z.string().min(1).max(100).optional(),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be YYYY-MM-DD"),
  administrativeSex: AdministrativeSexSchema,
  phone: z.string().min(3).max(40).optional(),
  email: z.string().email().max(160).optional(),
  chiefComplaint: z.string().min(2).max(500),
  urgency: z.enum(["routine", "soon", "urgent"]),
  coverage: WalkInCoverageSchema,
});
export type WalkInIntakeInput = z.infer<typeof WalkInIntakeInputSchema>;

export const CreateWalkInRequestSchema = z.object({
  siteId: UuidSchema,
  idempotencyKey: z.string().min(8).max(128),
  input: WalkInIntakeInputSchema,
});
export type CreateWalkInRequest = z.infer<typeof CreateWalkInRequestSchema>;

export const WalkInIntakeResultSchema = z.looseObject({
  created: z.boolean(),
  patient: BoardQueueRowSchema,
});
export type WalkInIntakeResult = z.infer<typeof WalkInIntakeResultSchema>;

/**
 * One api.list_patient_directory row. jsonb_strip_nulls means genuinely-null
 * fields (for example lastVisitAt) are absent, so those keys are optional and
 * the counts fall back to zero instead of failing the whole directory.
 */
export const PatientDirectoryEntrySchema = z.looseObject({
  id: UuidSchema,
  mrn: z.string().min(1),
  rosterPhysician: PhysicianSummarySchema.optional(),
  initials: z.string().min(1),
  displayName: z.string().min(1),
  age: z.number().int().nonnegative().optional(),
  /** Always emitted by the current RPC; optional so pre-migration payloads still parse. */
  administrativeSex: AdministrativeSexSchema.optional(),
  latestEncounterId: UuidSchema.optional(),
  latestEncounterStatus: z.string().optional(),
  lastVisitAt: z.string().optional(),
  /** Present for patients registered at the site (20260904120044); lastVisitAt falls back to it. */
  registeredAt: z.string().optional(),
  activeConditionCount: z.number().int().nonnegative().catch(0),
  activeMedicationCount: z.number().int().nonnegative().catch(0),
  documentCount: z.number().int().nonnegative().catch(0),
});
export type PatientDirectoryEntry = z.infer<typeof PatientDirectoryEntrySchema>;

/** Mirrors the GET /v1/patients/directory response envelope { items, total }. */
export const PatientDirectorySchema = z.looseObject({
  items: z.array(PatientDirectoryEntrySchema).catch([]),
  total: z.number().int().nonnegative().catch(0),
});
export type PatientDirectory = z.infer<typeof PatientDirectorySchema>;

/**
 * One api.list_patient_encounters row. jsonb_strip_nulls means genuinely-null
 * fields (an encounter with no appointment, an open visit with no end, an
 * unassigned clinician) are absent, so those keys are optional.
 */
export const PatientEncounterSummarySchema = z.looseObject({
  encounterId: UuidSchema,
  encounterVersion: z.number().int().positive(),
  appointmentId: UuidSchema.optional(),
  status: z.string().min(1),
  appointmentStatus: z.string().min(1).optional(),
  reason: z.string().catch("Clinic visit"),
  startedAt: z.string(),
  endedAt: z.string().optional(),
  noteStatus: z.string().catch("draft"),
  clinician: z.string().optional(),
});
export type PatientEncounterSummary = z.infer<typeof PatientEncounterSummarySchema>;

/** Mirrors the GET /v1/patients/:patientId/encounters envelope { items, total }. */
export const PatientEncounterHistorySchema = z.looseObject({
  items: z.array(PatientEncounterSummarySchema).catch([]),
  total: z.number().int().nonnegative().catch(0),
});
export type PatientEncounterHistory = z.infer<typeof PatientEncounterHistorySchema>;

// The chart-history writer contracts (exact p_* allow-lists of the three
// api.record_patient_* RPCs) and their receipt are shared with the agent-api
// and the Supabase adapter through @emr/contracts since 20260904014447.
// Patient profile (20260904020142): the role-projected read, the replace-set
// section writer and the demographics writer share one contract with the
// Agent API and the Supabase adapter.
export {
  type AllergyItem,
  type AllergyItemWrite,
  type ChartRecordReceipt,
  ChartRecordReceiptSchema,
  type ChartSourceKind,
  ChartSourceKindSchema,
  type DuplicatePatientMatch,
  type DuplicatePatientMatches,
  DuplicatePatientMatchesSchema,
  type DuplicatePatientProbe,
  DuplicatePatientProbeRequestSchema,
  DuplicatePatientProbeSchema,
  ENCOUNTER_DIAGNOSES_MAX,
  type EncounterDiagnosis,
  type EncounterDiagnosisCodeSystem,
  EncounterDiagnosisCodeSystemSchema,
  EncounterDiagnosisSchema,
  type EncounterDiagnosisSource,
  type EncounterDiagnosisWrite,
  EncounterDiagnosisWriteSchema,
  type FamilyHistoryRow,
  type FamilyHistoryRowWrite,
  type FamilyHistorySection,
  type FamilyHistorySectionWrite,
  type ImmunizationItem,
  type ImmunizationItemWrite,
  type MedicationItem,
  type MedicationItemWrite,
  PATIENT_PROFILE_SECTION_KEYS,
  type PatientCoverage,
  type PatientCoverageInput,
  type PatientDemographics,
  type PatientDemographicsUpdateInput,
  PatientDemographicsUpdateInputSchema,
  type PatientDemographicsUpdateReceipt,
  PatientDemographicsUpdateReceiptSchema,
  type PatientProfileSectionKey,
  PatientProfileSectionKeySchema,
  type PatientProfileSummary,
  PatientProfileSummarySchema,
  type PatientProfileUpdateInput,
  PatientProfileUpdateInputSchema,
  type PatientProfileUpdateReceipt,
  PatientProfileUpdateReceiptSchema,
  type PatientRosterProfile as PatientProfile,
  PatientRosterProfileSchema as PatientProfileSchema,
  type PatientSummaryAlert,
  type PatientSummaryLine,
  type PmhItem,
  type PmhItemWrite,
  patientProfileToDisplayStrings,
  type RecentEncounterDiagnosis,
  RecentEncounterDiagnosisSchema,
  type RecordPatientAllergyInput,
  RecordPatientAllergyInputSchema,
  type RecordPatientMedicationInput,
  RecordPatientMedicationInputSchema,
  type RecordPatientProblemInput,
  RecordPatientProblemInputSchema,
  type RegisterPatientInput,
  RegisterPatientInputSchema,
  type RegisterPatientReceipt,
  RegisterPatientReceiptSchema,
  type RegisterPatientRequest,
  RegisterPatientRequestSchema,
  type SaveEncounterDiagnosesReceipt,
  SaveEncounterDiagnosesReceiptSchema,
  type SaveEncounterDiagnosesRequest,
  SaveEncounterDiagnosesRequestSchema,
  type SocialHistoryItem,
  type SocialHistoryItemWrite,
} from "@emr/contracts";

/**
 * One api.list_patient_signed_notes row: the latest signed/amendment note per
 * prior encounter with full SOAP text. jsonb_strip_nulls drops null keys.
 */
export const PatientSignedNoteSchema = z.looseObject({
  noteId: UuidSchema,
  encounterId: UuidSchema,
  noteVersion: z.number().int().positive(),
  status: z.string().min(1),
  signedAt: z.string().optional(),
  clinician: z.string().optional(),
  encounterReason: z.string().optional(),
  encounterStartedAt: z.string().optional(),
  encounterEndedAt: z.string().optional(),
  contentHash: Sha256HashSchema,
  sections: z.looseObject({
    subjective: z.string().catch(""),
    objective: z.string().catch(""),
    assessment: z.string().catch(""),
    plan: z.string().catch(""),
  }),
  /** Ranked encounter diagnoses (20260906151703); absent on older databases. */
  diagnoses: z.array(EncounterDiagnosisSchema).optional(),
  /** Signed addenda to the note, oldest first (20261012235814); absent when it has none. */
  addenda: z.array(EncounterNoteAddendumSchema).optional(),
});
export type PatientSignedNote = z.infer<typeof PatientSignedNoteSchema>;

/** Mirrors the GET /v1/patients/:patientId/signed-notes envelope { items, total }. */
export const PatientSignedNotesSchema = z.looseObject({
  items: z.array(PatientSignedNoteSchema).catch([]),
  total: z.number().int().nonnegative().catch(0),
});
export type PatientSignedNotes = z.infer<typeof PatientSignedNotesSchema>;

/** Mirrors the p_input contract of api.start_patient_encounter exactly. */
export const StartPatientEncounterInputSchema = z.object({
  chiefComplaint: z.string().min(2).max(500),
  urgency: z.enum(["routine", "soon", "urgent"]),
  appointmentKind: z.literal("scheduled").optional(),
});
export type StartPatientEncounterInput = z.infer<typeof StartPatientEncounterInputSchema>;

export const StartPatientEncounterRequestSchema = z.object({
  siteId: UuidSchema,
  idempotencyKey: z.string().min(8).max(128),
  input: StartPatientEncounterInputSchema,
});
export type StartPatientEncounterRequest = z.infer<typeof StartPatientEncounterRequestSchema>;

/** Same receipt shape as the walk-in intake; created is false when an open visit was resumed. */
export const StartPatientEncounterResultSchema = z.looseObject({
  created: z.boolean(),
  patient: BoardQueueRowSchema,
});
export type StartPatientEncounterResult = z.infer<typeof StartPatientEncounterResultSchema>;

export const WorkspaceVitalSchema = z.looseObject({
  label: z.string(),
  value: z.string(),
  flag: z.enum(["high", "normal"]).catch("normal").optional(),
});
export type WorkspaceVital = z.infer<typeof WorkspaceVitalSchema>;

export const WorkspaceTimelineItemSchema = z.looseObject({
  id: z.string(),
  at: z.string(),
  title: z.string(),
  detail: z.string().catch(""),
  kind: z.enum(["encounter", "result", "medication", "message"]).catch("encounter"),
});
export type WorkspaceTimelineItem = z.infer<typeof WorkspaceTimelineItemSchema>;

export const WorkspaceNoteSectionsSchema = z.looseObject({
  subjective: z.string().catch(""),
  objective: z.string().catch(""),
  assessment: z.string().catch(""),
  plan: z.string().catch(""),
});
export type WorkspaceNoteSections = z.infer<typeof WorkspaceNoteSectionsSchema>;

export const WorkspaceResultSchema = z.looseObject({
  id: z.string(),
  reportVersion: z.number().int().optional(),
  kind: z.string().optional(),
  title: z.string(),
  summary: z.string().catch(""),
  workflowState: z.string().optional(),
  interpretation: z.string().optional(),
  receivedAt: z.string().optional(),
});
export type WorkspaceResult = z.infer<typeof WorkspaceResultSchema>;

export const WorkspaceDocumentSchema = z.looseObject({
  id: z.string(),
  title: z.string(),
  kind: z.string().optional(),
  mimeType: z.string().optional(),
  byteSize: z.number().int().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
});
export type WorkspaceDocument = z.infer<typeof WorkspaceDocumentSchema>;

export const WorkspaceCoverageDetailsSchema = z.looseObject({
  id: UuidSchema.optional(),
  version: z.number().int().optional(),
  payerName: z.string().nullish(),
  payerKind: CoveragePayerKindSchema.nullish(),
  memberId: z.string().nullish(),
  groupNumber: z.string().nullish(),
  relationship: z.string().nullish(),
  status: z.string().nullish(),
  eligibilityStatus: z.string().nullish(),
  lastVerifiedAt: z.string().nullish(),
});
export type WorkspaceCoverageDetails = z.infer<typeof WorkspaceCoverageDetailsSchema>;

/** Queue-row keys plus the api.get_patient_workspace extensions. */
export const PatientWorkspaceSchema = BoardQueueRowSchema.extend({
  tenantId: UuidSchema,
  siteId: UuidSchema,
  dob: z.string(),
  mrn: z.string(),
  allergies: z.array(z.string()).catch([]),
  medications: z.array(z.string()).catch([]),
  problems: z.array(z.string()).catch([]),
  /** Structured past medical history heads (20260906151703); the problems strings stay. */
  pmh: z.array(PmhItemSchema).optional(),
  /** The open encounter's ranked diagnoses (20260906151703). */
  encounterDiagnoses: z.array(EncounterDiagnosisSchema).optional(),
  /** Distinct codes across the patient's signed encounters, newest first (20260906151703). */
  recentEncounterDiagnoses: z.array(RecentEncounterDiagnosisSchema).optional(),
  vitals: z.array(WorkspaceVitalSchema).catch([]),
  timeline: z.array(WorkspaceTimelineItemSchema).catch([]),
  note: WorkspaceNoteSectionsSchema,
  noteVersion: z.number().int().nonnegative(),
  noteStatus: z.string(),
  results: z.array(WorkspaceResultSchema).catch([]),
  documents: z.array(WorkspaceDocumentSchema).catch([]),
  /** Formatted postal address from the chart, when recorded. */
  address: z.string().nullish(),
  /** The provincial/federal coverage member ID — the patient health number. */
  healthCardNumber: z.string().nullish(),
  coverageDetails: WorkspaceCoverageDetailsSchema.nullish(),
});
export type PatientWorkspace = z.infer<typeof PatientWorkspaceSchema>;

/**
 * The identifier a printed document labels for the patient: the health
 * number when the coverage is a provincial/federal plan, otherwise the private
 * plan's member ID. Undefined for cash patients.
 */
export function patientHealthIdentifier(
  workspace: Pick<PatientWorkspace, "healthCardNumber" | "coverageDetails">,
): { readonly label: string; readonly value: string; readonly note?: string } | undefined {
  const coverage = workspace.coverageDetails ?? undefined;
  const payerName = coverage?.payerName?.trim() || undefined;
  const healthCardNumber = workspace.healthCardNumber?.trim();
  if (healthCardNumber !== undefined && healthCardNumber !== "") {
    return {
      label: "Health Card #",
      value: healthCardNumber,
      ...(payerName ? { note: payerName } : {}),
    };
  }
  const memberId = coverage?.memberId?.trim();
  // An inactive plan's member ID is never printed, whatever its kind.
  if (memberId === undefined || memberId === "" || coverage?.status === "inactive") {
    return undefined;
  }
  const kind = coverage?.payerKind ?? undefined;
  const label =
    kind === "provincial_plan" || kind === "federal_program" ? "Health Card #" : "Member ID";
  return { label, value: memberId, ...(payerName ? { note: payerName } : {}) };
}

export const NoteSectionsWriteSchema = z.object({
  subjective: z.string(),
  objective: z.string(),
  assessment: z.string(),
  plan: z.string(),
});
export type NoteSectionsWrite = z.infer<typeof NoteSectionsWriteSchema>;

export const EncounterNoteWriteRequestSchema = z.object({
  expectedEncounterVersion: z.number().int().positive(),
  expectedNoteVersion: z.number().int().nonnegative(),
  sections: NoteSectionsWriteSchema,
});
export type EncounterNoteWriteRequest = z.infer<typeof EncounterNoteWriteRequestSchema>;

export const EncounterNoteWriteReceiptSchema = z.looseObject({
  encounterId: UuidSchema,
  encounterVersion: z.number().int().positive(),
  noteId: UuidSchema,
  noteVersion: z.number().int().positive(),
  noteStatus: z.string(),
  contentHash: z.string(),
  savedAt: z.string(),
});
export type EncounterNoteWriteReceipt = z.infer<typeof EncounterNoteWriteReceiptSchema>;

export const WorkforceProfileSchema = z.looseObject({
  userId: UuidSchema,
  email: z.string(),
  displayName: z.string(),
  role: z.string(),
  tenantId: UuidSchema,
  siteId: UuidSchema,
  organizationName: z.string(),
  siteName: z.string(),
  profileVersion: z.number().int().positive(),
  canManage: z.boolean(),
  licenseNumber: z.string().optional(),
  licenseJurisdiction: z.string().optional(),
  signatureName: z.string().optional(),
  // Letterhead fields for printed documents (absent until the site records them).
  siteAddress: z.string().optional(),
  sitePhone: z.string().optional(),
  siteFax: z.string().optional(),
  siteTimezone: z.string().optional(),
});
export type WorkforceProfile = z.infer<typeof WorkforceProfileSchema>;

/** Mirrors GET /v1/site/contact: the structured letterhead fields for the site. */
export const SiteContactSchema = z.looseObject({
  tenantId: UuidSchema,
  siteId: UuidSchema,
  name: z.string(),
  timezone: z.string(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  address: z.looseObject({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
  }),
  addressFormatted: z.string().nullable(),
});
export type SiteContact = z.infer<typeof SiteContactSchema>;

/** Mirrors PUT /v1/site/contact (strict): every field replaces the stored value. */
export const UpdateSiteContactRequestSchema = z.object({
  phone: z.string().trim().max(40).nullable().optional(),
  fax: z.string().trim().max(40).nullable().optional(),
  address: z
    .object({
      line1: z.string().trim().max(120).nullable().optional(),
      line2: z.string().trim().max(120).nullable().optional(),
      city: z.string().trim().max(120).nullable().optional(),
      region: z.string().trim().max(120).nullable().optional(),
      postalCode: z.string().trim().max(20).nullable().optional(),
      country: z.string().trim().max(120).nullable().optional(),
    })
    .optional(),
});
export type UpdateSiteContactRequest = z.infer<typeof UpdateSiteContactRequestSchema>;

export const WorkforceMemberSchema = z.looseObject({
  userId: UuidSchema,
  email: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  role: z.string(),
  active: z.boolean(),
  licenseNumber: z.string().nullable().optional(),
  licenseJurisdiction: z.string().nullable().optional(),
  profileVersion: z.number().int().positive(),
  sites: z.array(z.looseObject({ siteId: UuidSchema, name: z.string() })),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkforceMember = z.infer<typeof WorkforceMemberSchema>;

export const WorkforceInvitationSchema = z.looseObject({
  id: UuidSchema,
  siteId: UuidSchema,
  email: z.string(),
  displayName: z.string(),
  role: z.string(),
  status: z.enum(["staged", "sent", "failed", "accepted", "revoked"]),
  invitedAt: z.string(),
  expiresAt: z.string(),
  expired: z.boolean().optional(),
  failureCode: z.string().nullable().optional(),
});
export type WorkforceInvitation = z.infer<typeof WorkforceInvitationSchema>;

export const CreateWorkforceInvitationRequestSchema = z.object({
  siteId: UuidSchema,
  idempotencyKey: z.string().min(8).max(128),
  email: z.string().min(3).max(240),
  displayName: z.string().min(2).max(160),
  role: z.enum([
    "physician",
    "nurse_ma",
    "front_desk",
    "referral_coordinator",
    "billing",
    "clinic_admin",
    "auditor",
  ]),
});
export type CreateWorkforceInvitationRequest = z.infer<
  typeof CreateWorkforceInvitationRequestSchema
>;

export const WorkforceInvitationOutcomeSchema = z.looseObject({
  invitation: WorkforceInvitationSchema,
  delivery: z.enum(["sent", "failed", "none"]),
});
export type WorkforceInvitationOutcome = z.infer<typeof WorkforceInvitationOutcomeSchema>;

export const WorkforceMembershipStatusOutcomeSchema = z.looseObject({
  membership: z.looseObject({
    userId: UuidSchema,
    role: z.string(),
    active: z.boolean(),
    displayName: z.string().nullable().optional(),
    updatedAt: z.string(),
  }),
  sessionsRevoked: z.boolean(),
});
export type WorkforceMembershipStatusOutcome = z.infer<
  typeof WorkforceMembershipStatusOutcomeSchema
>;

/**
 * Mirrors api.update_current_workforce_profile parameters (site comes from
 * x-site-id). For the optional credential fields an omitted key preserves the
 * stored value and an empty string clears it.
 */
export const UpdateWorkforceProfileRequestSchema = z.object({
  expectedVersion: z.number().int().positive(),
  displayName: z.string().min(2).max(160),
  licenseNumber: z.union([z.literal(""), z.string().min(2).max(80)]).optional(),
  licenseJurisdiction: z.union([z.literal(""), z.string().min(2).max(120)]).optional(),
  signatureName: z.union([z.literal(""), z.string().min(2).max(160)]).optional(),
});
export type UpdateWorkforceProfileRequest = z.infer<typeof UpdateWorkforceProfileRequestSchema>;

/** A prescription lists at most this many medications (RX-3). */
export const MAX_PRESCRIPTION_ITEMS = 10;

/**
 * One medication on a printable prescription. The RPC rejects any
 * controlledSchedule or pharmacyDestinationId key; the remaining fields are
 * stored verbatim.
 */
export const PrintablePrescriptionItemSchema = z.object({
  medicationDisplay: z.string().min(2).max(200),
  sig: z.string().min(2).max(500),
  quantity: z.number().positive(),
  quantityUnit: z.string().min(1).max(40),
  refills: z.number().int().min(0).max(12),
  indication: z.string().min(2).max(240),
  substitutionAllowed: z.boolean(),
  daysSupply: z.number().int().positive().max(365).optional(),
  /** An FDB MEDID picked from MedKnowledge; the server screens coded drugs. */
  medicationCode: z
    .string()
    .regex(/^\d{1,9}$/)
    .optional(),
  medicationCodeSystem: z.literal("FDB_MEDID").optional(),
  structuredSig: StructuredSigSchema.optional(),
  /** A refill (EXECDEV-101): the signed prescription item this medication renews. */
  renewalOf: PrescriptionRenewalLinkSchema.optional(),
  /** A change (EXECDEV-176): the signed prescription item this medication replaces. */
  changeOf: PrescriptionChangeLinkSchema.optional(),
});
export type PrintablePrescriptionItem = z.infer<typeof PrintablePrescriptionItemSchema>;

/**
 * The prescription of the visit (RX-3): every medication it lists, signed as
 * one document. The RPC requires source "manual" and nothing else beside the
 * items; a refill (EXECDEV-101) signs with operation "renew", a change
 * (EXECDEV-176) with "change".
 */
export const PrintablePrescriptionItemsDetailsSchema = z.object({
  operation: z.enum(["new", "renew", "change"]),
  source: z.literal("manual"),
  items: z.array(PrintablePrescriptionItemSchema).min(1).max(MAX_PRESCRIPTION_ITEMS),
});
export type PrintablePrescriptionItemsDetails = z.infer<
  typeof PrintablePrescriptionItemsDetailsSchema
>;

/**
 * Free-form clinical fields of the single-medication printable prescription
 * (the flat form the RPC still accepts). The RPC requires operation "new",
 * source "manual", and rejects any controlledSchedule or pharmacyDestinationId
 * key; the remaining fields are stored verbatim.
 */
export const PrintablePrescriptionDetailsSchema = PrintablePrescriptionItemSchema.extend({
  operation: z.literal("new"),
  source: z.literal("manual"),
});
export type PrintablePrescriptionDetails = z.infer<typeof PrintablePrescriptionDetailsSchema>;

/**
 * Mirrors the POST /v1/encounters/:encounterId/printable-prescriptions body
 * (strict). The encounter comes from the URL path; the body carries the site.
 * controlledSchedule and pharmacyDestinationId must stay absent (fail closed).
 */
export const PrintablePrescriptionRequestSchema = z.object({
  siteId: UuidSchema,
  idempotencyKey: z.string().min(8).max(128),
  patient: z.object({
    patientId: UuidSchema,
    displayName: z.string().min(1).max(200),
    dateOfBirth: z.string().min(4).max(40),
    mrn: z.string().min(1).max(80).optional(),
  }),
  /** The flat single-medication form, or every medication in items[] (RX-3). */
  prescription: z.union([
    PrintablePrescriptionDetailsSchema,
    PrintablePrescriptionItemsDetailsSchema,
  ]),
  safetyEvaluation: z.object({
    valid: z.literal(true),
    evaluatedAt: z.string().min(1),
    summary: z.string().min(1).max(500),
    /**
     * Reasons for the screening alerts the server requires an override for:
     * here for the flat form, per medication in items[] (same index) otherwise.
     */
    overrides: z.array(FdbScreeningOverrideSchema).max(50).optional(),
    items: z
      .array(z.object({ overrides: z.array(FdbScreeningOverrideSchema).max(50).optional() }))
      .max(MAX_PRESCRIPTION_ITEMS)
      .optional(),
  }),
  contentHash: Sha256HashSchema,
  documentHash: Sha256HashSchema,
  documentBase64: z.string().min(8),
  fileName: z.string().min(1).max(240),
});
export type PrintablePrescriptionRequest = z.infer<typeof PrintablePrescriptionRequestSchema>;

export const PrintablePrescriptionRecordSchema = z.looseObject({
  printablePrescriptionId: UuidSchema,
  version: z.number().int().positive(),
  encounterId: UuidSchema,
  patientId: UuidSchema,
  status: z.string(),
  deliveryMethod: z.string(),
  signedAt: z.string(),
  contentHash: Sha256HashSchema,
  documentHash: Sha256HashSchema,
  mimeType: z.string(),
  fileName: z.string(),
  documentBase64: z.string(),
  verification: z.enum(["verified", "failed"]).optional(),
  prescriber: z
    .looseObject({
      userId: UuidSchema.optional(),
      displayName: z.string().optional(),
      signatureName: z.string().optional(),
      licenseNumber: z.string().optional(),
      licenseJurisdiction: z.string().optional(),
      organizationName: z.string().optional(),
      siteName: z.string().optional(),
    })
    .optional(),
  prescription: z.unknown().optional(),
  safetyEvaluation: z.unknown().optional(),
  /**
   * An items[] prescription's receipt: each medication with the chart entry
   * signing added for it. Flat prescriptions report chartMedication instead.
   */
  items: z.unknown().optional(),
  /**
   * A listed prescription (EXECDEV-176): what became of each medication since
   * it was signed (active, cancelled or changed). Read through
   * prescriptionItemStates, which drops a shape it does not know.
   */
  itemStates: z.unknown().optional(),
});
export type PrintablePrescriptionRecord = z.infer<typeof PrintablePrescriptionRecordSchema>;

/**
 * Mirrors the POST /v1/patients/:patientId/prescription-renewals response: a
 * refill signed outside a visit, and the "Prescription renewal" encounter the
 * server opened, signed and closed for it (EXECDEV-101).
 */
export const PrescriptionRenewalReceiptSchema = z.object({
  prescription: PrintablePrescriptionRecordSchema,
  renewalVisit: PrescriptionRenewalVisitSchema,
});
export type PrescriptionRenewalReceipt = z.infer<typeof PrescriptionRenewalReceiptSchema>;

/**
 * Mirrors the POST /v1/patients/:patientId/prescription-changes response: a
 * change signed outside a visit, and the "Prescription change" encounter the
 * server opened, signed and closed for it (EXECDEV-176).
 */
export const PrescriptionChangeReceiptSchema = z.object({
  prescription: PrintablePrescriptionRecordSchema,
  changeVisit: PrescriptionChangeVisitSchema,
});
export type PrescriptionChangeReceipt = z.infer<typeof PrescriptionChangeReceiptSchema>;

/**
 * Mirrors the GET /v1/encounters/:encounterId/printable-prescriptions response.
 * Every entry carries its stored PDF, so a resumed encounter can reprint the
 * exact documents it signed.
 */
export const PrintablePrescriptionListSchema = z.array(PrintablePrescriptionRecordSchema);
export type PrintablePrescriptionList = z.infer<typeof PrintablePrescriptionListSchema>;

export {
  type AssignAppointmentRoomRequest,
  AssignAppointmentRoomRequestSchema,
  type ClinicRoom,
  ClinicRoomSchema,
  ClinicRoomsSchema,
  type CreateClinicRoomRequest,
  CreateClinicRoomRequestSchema,
  type CreateStaffRequestRequest,
  CreateStaffRequestRequestSchema,
  type DeleteClinicRoomRequest,
  DeleteClinicRoomRequestSchema,
  DeletedClinicRoomSchema,
  type ReleaseAppointmentRoomRequest,
  ReleaseAppointmentRoomRequestSchema,
  type StaffRequest,
  StaffRequestSchema,
  StaffRequestsSchema,
  type TodayBoardMetrics,
  TodayBoardMetricsSchema,
  type TransitionStaffRequestRequest,
  TransitionStaffRequestRequestSchema,
  type UpdateClinicRoomRequest,
  UpdateClinicRoomRequestSchema,
} from "@emr/contracts";
